import { capturePosthogEvent } from '@civilgrid/ui/analytics';
import type { GeoreferencedMapAccess } from '~/api/georeferencedMaps';
import type { ConnectbaseNoticeStatus, ConnectbaseTaskKey } from '~/api/projectConnectbaseTasks';
import type { generateEvidenceEmailChain } from '~/api/projects2';
import type { MapObject } from '~/common/types';

export { capturePosthogEvent };

export const POSTHOG_EVENTS = {
  layerToggled: 'map_layer_toggled',
  layerSectionToggled: 'map_layer_section_toggled',
  layerPresetToggled: 'map_layer_preset_toggled',
  layersAllToggled: 'map_layers_all_toggled',
  layerIsolated: 'map_layer_isolated',
  layerIsolateUndone: 'map_layer_isolate_undone',
  georeferencedMapSourcePdfOpened: 'georeferenced_map_source_pdf_opened',
  georeferencedMapSourceDeepLinkPdfOpened: 'georeferenced_map_source_deep_link_pdf_opened',
  stateZoomApplied: 'map_state_zoom_applied',
  civilsearchGeojsonOpened: 'civilsearch_geojson_opened',
  civilsearchAgencyMapTabOpened: 'civilsearch_agency_map_tab_opened',
  civilsearchAgencyMapFeatureZoomed: 'civilsearch_agency_map_feature_zoomed',
  civilsearchAgencySubGeometriesViewed: 'civilsearch_agency_sub_geometries_viewed',
  civilsearchAgencySubGeometrySelected: 'civilsearch_agency_sub_geometry_selected',
  civilsearchAgencySubGeometryDrawStarted: 'civilsearch_agency_sub_geometry_draw_started',
  civilsearchAgencySubGeometryCreated: 'civilsearch_agency_sub_geometry_created',
  civilsearchAgencySubGeometryEditStarted: 'civilsearch_agency_sub_geometry_edit_started',
  civilsearchAgencySubGeometryUpdated: 'civilsearch_agency_sub_geometry_updated',
  civilsearchAgencySubGeometryOverlapBlocked: 'civilsearch_agency_sub_geometry_overlap_blocked',
  civilsearchAgencySubGeometryOutOfBoundsWarned: 'civilsearch_agency_sub_geometry_out_of_bounds_warned',
  civilsearchAgencySubGeometryMemberAssigned: 'civilsearch_agency_sub_geometry_member_assigned',
  civilsearchAgencySubGeometryMemberUnassigned: 'civilsearch_agency_sub_geometry_member_unassigned',
  civilsearchAgencySubGeometryNoteSaved: 'civilsearch_agency_sub_geometry_note_saved',
  civilsearchAgencySubGeometryArchived: 'civilsearch_agency_sub_geometry_archived',
  civilsearchAgencyHistoryTabOpened: 'civilsearch_agency_history_tab_opened',
  civilsearchMissiveConnected: 'civilsearch_missive_connected',
  civilsearchMissiveEmailSearched: 'civilsearch_missive_email_searched',
  agencyMergeDialogOpened: 'agency_merge_dialog_opened',
  agencyMergeDuplicatesToggled: 'agency_merge_duplicates_toggled',
  agencyMergeFieldValuePicked: 'agency_merge_field_value_picked',
  agencyMergeFieldValueTyped: 'agency_merge_field_value_typed',
  agencyMergeAgencyRemoved: 'agency_merge_agency_removed',
  agencyMergeBlockedHusk: 'agency_merge_blocked_husk',
  agencyMergeAuditViewed: 'agency_merge_audit_viewed',
  agencyMergeSubmitted: 'agency_merge_submitted',
  permitPdfRequested: 'permit_pdf_requested',
  permitDraftSaved: 'permit_draft_saved',
  permitDraftDeleted: 'permit_draft_deleted',

  reportProblemModalOpened: 'report_problem_modal_opened',
  reportProblemSubmitted: 'report_problem_submitted',

  conflictsSummaryYearFilterChanged: 'conflicts_summary_year_filter_changed',
  conflictsSummaryMyProjectsToggled: 'conflicts_summary_my_projects_toggled',
  conflictsSummarySearchChanged: 'conflicts_summary_search_changed',
  conflictsSummaryPlanStatusChanged: 'conflicts_summary_plan_status_changed',
  conflictsSummaryConstructionDateChanged: 'conflicts_summary_construction_date_changed',
  conflictsSummaryNotesUpdated: 'conflicts_summary_notes_updated',
  conflictsSummaryProjectOpened: 'conflicts_summary_project_opened',
  conflictsSummarySortChanged: 'conflicts_summary_sort_changed',
  conflictsSummaryExportClicked: 'conflicts_summary_export_clicked',
  conflictsSummaryExportSucceeded: 'conflicts_summary_export_succeeded',
  conflictsSummaryExportFailed: 'conflicts_summary_export_failed',
  conflictsSummaryFeatureRowsToggled: 'conflicts_summary_feature_rows_toggled',
  conflictsSummaryConstructionDatesBulkUpdated: 'conflicts_summary_construction_dates_bulk_updated',
  conflictsSummaryConstructionDatesBulkUpdateFailed: 'conflicts_summary_construction_dates_bulk_update_failed',
  conflictsSummaryEditDatesDialogOpened: 'conflicts_summary_edit_dates_dialog_opened',
  conflictsSummaryEditPlanStatusDialogOpened: 'conflicts_summary_edit_plan_status_dialog_opened',
  conflictsSummaryEditPlanStatusSelected: 'conflicts_summary_edit_plan_status_selected',
  conflictsSummaryPlanStatusBulkUpdated: 'conflicts_summary_plan_status_bulk_updated',
  conflictsSummaryPlanStatusBulkUpdateFailed: 'conflicts_summary_plan_status_bulk_update_failed',

  // New Conflict Summary page (/conflictsSummaryV2, behind new-conflict-summary-enabled)
  conflictsSummaryV2Viewed: 'conflicts_summary_v2_viewed',
  conflictsSummaryV2TryNewClicked: 'conflicts_summary_v2_try_new_clicked',
  conflictsSummaryV2BackToClassicClicked: 'conflicts_summary_v2_back_to_classic_clicked',

  // Data Refresh Status page (/conflictsSummary/dataRefreshStatus)
  conflictsSummaryDataRefreshStatusClicked: 'conflicts_summary_data_refresh_status_clicked',
  dataRefreshStatusViewed: 'data_refresh_status_viewed',
  dataRefreshStatusSortChanged: 'data_refresh_status_sort_changed',
  dataRefreshStatusFiltersChanged: 'data_refresh_status_filters_changed',
  dataRefreshStatusSearchChanged: 'data_refresh_status_search_changed',
  dataRefreshStatusPageChanged: 'data_refresh_status_page_changed',

  // Project surface — map controls
  projectBasemapChanged: 'project_basemap_changed',
  projectMapViewToggled: 'project_map_view_toggled',
  projectLayerFilterChanged: 'project_layer_filter_changed',

  // Project surface — layer toggles
  projectEnvironmentalLayerToggled: 'project_environmental_layer_toggled',
  projectGeotechLayerToggled: 'project_geotech_layer_toggled',

  // Project surface — CivilDraw
  projectCivildrawDraftCreated: 'project_civildraw_draft_created',
  projectCivildrawDrawingCreated: 'project_civildraw_drawing_created',
  projectCivildrawObjectAdded: 'project_civildraw_object_added',
  projectCivildrawPrintCreated: 'project_civildraw_print_created',

  // Project surface — downloads & exports
  projectFileDownloaded: 'project_file_downloaded',
  projectFileDownloadFailed: 'project_file_download_failed',
  projectProposalPacketExported: 'project_proposal_packet_exported',
  projectProposalPacketExportFailed: 'project_proposal_packet_export_failed',
  projectExportInitiated: 'project_export_initiated',
  projectExportFailed: 'project_export_failed',
  projectExportGuideOpened: 'project_export_guide_opened',
  exportHistoryLoaded: 'export_history_loaded',
  exportTabLoaded: 'export_tab_loaded',
  exportHistorySlowLoadDetected: 'export_history_slow_load_detected',
  exportHistoryAwaitingSubmissionTimedOut: 'export_history_awaiting_submission_timed_out',
  exportFormDataLoadFailed: 'export_form_data_load_failed',
  exportFormProjectSlowLoadDetected: 'export_form_project_slow_load_detected',
  exportLimitsCheckRetried: 'export_limits_check_retried',
  exportCustomDataLoadFailed: 'export_custom_data_load_failed',
  exportLayerSearchChanged: 'export_layer_search_changed',
  exportLayersBulkToggledOn: 'export_layers_bulk_toggled_on',
  exportLayersBulkToggledOff: 'export_layers_bulk_toggled_off',
  exportFormRebuilt: 'export_form_rebuilt',

  // Project surface — field notes & intelligence
  projectFieldNoteAdded: 'project_field_note_added',
  projectFieldNoteOpened: 'project_field_note_opened',
  projectFieldNoteEdited: 'project_field_note_edited',
  projectFieldNoteDeleted: 'project_field_note_deleted',
  projectFieldNotesExported: 'project_field_notes_exported',
  projectFieldNotesTabViewed: 'project_field_notes_tab_viewed',
  projectFieldNotePhotoFullscreenToggled: 'project_field_note_photo_fullscreen_toggled',
  projectFieldNotePhotoZoomed: 'project_field_note_photo_zoomed',
  projectFieldNotePhotoPanned: 'project_field_note_photo_panned',
  projectPhotoImportOpened: 'project_photo_import_opened',
  projectPhotoImportZipRead: 'project_photo_import_zip_read',
  projectPhotoImportStarted: 'project_photo_import_started',
  projectPhotoImportCompleted: 'project_photo_import_completed',
  projectIntelligenceTabViewed: 'project_intelligence_tab_viewed',

  // Fires from TOC picker, per-layer detail panel, and project info panel —
  // disambiguated by metadata_source.
  projectMetadataOpened: 'project_metadata_opened',

  // Layer Library (CG-512) — the per-user layer settings surface.
  layerLibraryOpened: 'layer_library_opened',
  layerLibraryClosed: 'layer_library_closed',
  layerLibraryLayerAdded: 'layer_library_layer_added',
  layerLibraryLayerRemoved: 'layer_library_layer_removed',
  layerLibraryReset: 'layer_library_reset',
  layerLibraryScopeChanged: 'layer_library_scope_changed',
  layerLibrarySearched: 'layer_library_searched',
  // Cross-link from the user page to the org-admin default-layer settings (CG-724).
  layerLibraryOrgLinkClicked: 'layer_library_org_link_clicked',

  // Org Layer Library (CG-724) — the org-admin default-layer settings surface.
  orgLayerLibraryOpened: 'org_layer_library_opened',
  orgLayerLibraryLayerAdded: 'org_layer_library_layer_added',
  orgLayerLibraryLayerRemoved: 'org_layer_library_layer_removed',
  orgLayerLibraryReset: 'org_layer_library_reset',
  orgLayerLibraryScopeChanged: 'org_layer_library_scope_changed',
  orgLayerLibrarySearched: 'org_layer_library_searched',

  // Layer picker — bookmark + section expansion (CG-512). Sit in the existing
  // `map_layer_*` family alongside the visibility toggles in Layers/handlers.ts.
  layerPinned: 'map_layer_pinned',
  layerUnpinned: 'map_layer_unpinned',
  layerSectionExpanded: 'map_layer_section_expanded',

  // Confidential-project warning (data-request narrative step)
  projectConfidentialWarningShown: 'project_confidential_warning_shown',
  projectConfidentialWarningResolved: 'project_confidential_warning_resolved',

  // Project surface — admin custom-data section (Project Files panel).
  customDataUploadOpened: 'custom_data_upload_opened',
  customDataWmtsSubmitted: 'custom_data_wmts_submitted',
  // Client-side submit action; distinct from the server's `custom_data_shapefile_uploaded`.
  customDataShapefileSubmitted: 'custom_data_shapefile_submitted',
  // Shapefile upload carried at least one category color the uploader changed from the default.
  customDataCategoryColorsSet: 'custom_data_category_colors_set',
  // Admin opened the recolor dialog on an already-ingested shapefile layer.
  customDataCategoryColorsOpened: 'custom_data_category_colors_opened',
  // Map metadata popup rendered a custom-data shapefile feature.
  customDataFeatureInspected: 'custom_data_feature_inspected',
  // Admin blurred the Capabilities URL field and the inspect call returned successfully.
  customDataWmtsInspected: 'custom_data_wmts_inspected',

  // Admin tool — organization special datasets
  orgSpecialDatasetsUpdated: 'org_special_datasets_updated',

  // Admin tool — global layer enable/disable
  tileLayersDisabledUpdated: 'tile_layers_disabled_updated',

  // Admin tool — demo org reset (super-admin confirmed the destructive reset)
  demoOrgResetRequested: 'demo_org_reset_requested',

  // CivilDraw (formerly "GEK") — map/object/comment/file actions
  civildrawMapCreated: 'project_civildraw_map_created',
  civildrawMapDeleted: 'project_civildraw_map_deleted',
  civildrawMapDuplicated: 'project_civildraw_map_duplicated',
  civildrawUnitsToggled: 'project_civildraw_units_toggled',
  civildrawOpenedInGoogleMaps: 'project_civildraw_opened_in_google_maps',
  civildrawActionUndone: 'project_civildraw_action_undone',
  civildrawActionRedone: 'project_civildraw_action_redone',
  civildrawObjectDeleted: 'project_civildraw_object_deleted',
  civildrawObjectVisibilityToggled: 'project_civildraw_object_visibility_toggled',
  civildrawAllObjectsVisibilityToggled: 'project_civildraw_all_objects_visibility_toggled',
  civildrawObjectStylingUpdated: 'project_civildraw_object_styling_updated',
  civildrawObjectGeometryUpdated: 'project_civildraw_object_geometry_updated',
  civildrawObjectFlownTo: 'project_civildraw_object_flown_to',
  civildrawCommentAdded: 'project_civildraw_comment_added',
  civildrawCommentDeleted: 'project_civildraw_comment_deleted',
  civildrawCoordinatesCopied: 'project_civildraw_coordinates_copied',
  civildrawObjectCoordinatesCopied: 'project_civildraw_object_coordinates_copied',
  civildrawFileAdded: 'project_civildraw_file_added',
  civildrawFileDeleted: 'project_civildraw_file_deleted',
  civildrawFileDownloaded: 'project_civildraw_file_downloaded',
  civildrawFileDownloadFailed: 'project_civildraw_file_download_failed',
  civildrawFileImported: 'project_civildraw_file_imported',

  // Map widgets
  mapExported: 'map_exported',
  mapExportFailed: 'map_export_failed',
  mapHistoricalImageryViewed: 'map_historical_imagery_viewed',
  mapMetadataLayerError: 'map_metadata_layer_error',
  mapMetadataLinkOpened: 'map_metadata_link_opened',
  mapLayersInView: 'map_layers_in_view',
  mapMetadataInView: 'map_metadata_in_view',

  // Verification
  verificationLearnMoreClicked: 'verification_learn_more_clicked',
  verificationStarted: 'verification_started',
  verificationSkipped: 'verification_skipped',
  verificationCancelled: 'verification_cancelled',
  verificationCompleted: 'verification_completed',

  // Organization & user
  organizationInviteResent: 'organization_invite_resent',
  organizationInviteRevoked: 'organization_invite_revoked',
  organizationLogoUploaded: 'organization_logo_uploaded',
  organizationPermissionUpdated: 'organization_permission_updated',
  organizationRoleUpdated: 'organization_role_updated',
  organizationJoined: 'organization_joined',
  organizationCreated: 'organization_created',
  organizationCreateTabViewed: 'organization_create_tab_viewed',
  organizationJoinViewed: 'organization_join_viewed',
  organizationBillingSummaryPrinted: 'organization_billing_summary_printed',
  organizationBillingSummaryPrintFailed: 'organization_billing_summary_print_failed',
  organizationTestEnvOverrideToggled: 'organization_test_env_override_toggled',
  organizationConnectbaseLookupToggled: 'organization_connectbase_lookup_toggled',
  userProfileUpdated: 'user_profile_updated',
  userProfileViewed: 'user_profile_viewed',
  supportPageOpened: 'support_page_opened',
  organizationInvoicePrinted: 'organization_invoice_printed',
  organizationInvoicePrintFailed: 'organization_invoice_print_failed',
  contactSupportClicked: 'contact_support_clicked',

  // Search
  universalSearchOpened: 'universal_search_opened',
  universalSearchResultClicked: 'universal_search_result_clicked',
  parcelSearched: 'parcel_searched',
  parcelSearchResultsReturned: 'parcel_search_results_returned',
  coordinateSearched: 'coordinate_searched',

  // Announcements & onboarding
  announcementViewed: 'announcement_viewed',
  announcementPageViewed: 'announcement_page_viewed',
  announcementCtaClicked: 'announcement_cta_clicked',
  productUpdateViewed: 'product_update_viewed',
  productUpdateDismissed: 'product_update_dismissed',
  onboardingStepViewed: 'onboarding_step_viewed',

  // New project flow & project details
  newProjectFlowInitiated: 'new_project_flow_initiated',
  newProjectCreated: 'new_project_created',
  projectDetailUpdated: 'project_detail_updated',
  projectBoundaryEditViewed: 'project_boundary_edit_viewed',
  projectBoundarySaved: 'project_boundary_saved',
  projectBoundaryDiscarded: 'project_boundary_discarded',
  projectQuotePrinted: 'project_quote_printed',
  projectQuotePrintFailed: 'project_quote_print_failed',
  projectReceiptPrinted: 'project_receipt_printed',
  projectReceiptPrintFailed: 'project_receipt_print_failed',
  projectBookmarked: 'project_bookmarked',
  projectTeamMemberRemoved: 'project_team_member_removed',
  projectManagerSelfAssigned: 'project_manager_self_assigned',
  projectBoundaryKMZUploaded: 'project_boundary_kmz_uploaded',
  projectBoundaryKMZUploadFailed: 'project_boundary_kmz_upload_validation_failed',

  // Files & media
  projectFileUploaded: 'project_file_uploaded',
  projectFileDeleted: 'project_file_deleted',
  projectFileViewed: 'project_file_viewed',

  // Environmental / geotech / land rights / zoning / field notes
  environmentalSwpppExported: 'environmental_swppp_exported',
  environmentalSwpppExportFailed: 'environmental_swppp_export_failed',
  femaFloodReportGenerated: 'fema_flood_report_generated',
  femaFloodReportGenerationFailed: 'fema_flood_report_generation_failed',
  geotechnicalStudyOrdered: 'geotechnical_study_ordered',
  landRightsDataDownloaded: 'land_rights_data_downloaded',
  landRightsDataDownloadFailed: 'land_rights_data_download_failed',
  zoningDataDownloaded: 'zoning_data_downloaded',
  environmentalStudyPDFDownloaded: 'environmental_study_pdf_downloaded',
  environmentalStudyPDFDownloadFailed: 'environmental_study_pdf_download_failed',

  // Utilities (legacy) & CivilSearch (utilities V2)
  utilitiesModuleRequested: 'utilities_module_requested',
  utilitiesContactsDownloaded: 'utilities_contacts_downloaded',
  utilitiesOutreachLogExported: 'utilities_outreach_log_exported',
  utilitiesTabLoaded: 'utilities_tab_loaded',
  utilitiesTabSlowLoadDetected: 'utilities_tab_slow_load_detected',
  utilitiesEstTimeSortChanged: 'utilities_est_time_sort_changed',
  utilitiesFileDownloaded: 'utilities_file_downloaded',
  utilitiesNotificationPreferencesUpdated: 'utilities_notification_preferences_updated',
  connectbaseStatusMessageShown: 'connectbase_status_message_shown',
  civilsearchRequestSubmitted: 'civilsearch_request_submitted',
  civilsearchRequestDataViewed: 'civilsearch_request_data_viewed',
  civilsearchRequestResearchClicked: 'civilsearch_request_research_clicked',
  civilsearchRequestDetailsViewed: 'civilsearch_request_details_viewed',
  civilsearchSiblingRequestClicked: 'civilsearch_sibling_request_clicked',
  civilsearchMessageAdded: 'civilsearch_message_added',
  civilsearchFileClicked: 'civilsearch_file_clicked',
  civilsearchUtilityContactsViewed: 'civilsearch_utility_contacts_viewed',
  utilitiesContactEmailCopied: 'utilities_contact_email_copied',
  utilitiesContactEmailClicked: 'utilities_contact_email_clicked',
  utilitiesContactsZeroStateViewed: 'utilities_contacts_zero_state_viewed',
  utilitiesContactsSearched: 'utilities_contacts_searched',
  utilitiesContactsTypeFilterChanged: 'utilities_contacts_type_filter_changed',
  civilsearchTableExpandToggled: 'civilsearch_table_expand_toggled',
  civilsearchSubAgencyRowToggled: 'civilsearch_subagency_row_toggled',
  civilsearchOutreachLogExported: 'civilsearch_outreach_log_exported',
  civilsearchAgencyCommunicationLogGenerated: 'civilsearch_agency_communication_log_generated',
  civilsearchRecordsResearchReportGenerated: 'civilsearch_records_research_report_generated',
  utilitiesExportsMenuItemSelected: 'utilities_exports_menu_item_selected',
  utilitiesReportsMovedPopoverOpened: 'utilities_reports_moved_popover_opened',
  utilitiesReportsMovedPopoverClosed: 'utilities_reports_moved_popover_closed',
  projectAgencyHistoryOpened: 'project_agency_history_opened',
  projectUtilitiesAttachmentsDownloaded: 'project_utilities_attachments_downloaded',
  projectUtilitiesAttachmentsDownloadFailed: 'project_utilities_attachments_download_failed',

  // Intelligence
  intelligenceStatusUpdated: 'intelligence_status_updated',
  intelligenceFeatureSelected: 'intelligence_feature_selected',
  intelligenceCommentAdded: 'intelligence_comment_added',
  intelligenceCommentArchived: 'intelligence_comment_archived',
  intelligenceSectionExpanded: 'intelligence_section_expanded',
  intelligenceSubsectionExpanded: 'intelligence_subsection_expanded',
  intelligenceComputeFailed: 'intelligence_compute_failed',
  intelligenceAgencyPdfDownloaded: 'intelligence_agency_pdf_downloaded',
  intelligenceAgencyPdfDownloadFailed: 'intelligence_agency_pdf_download_failed',
  intelligenceAgencyLinkOpened: 'intelligence_agency_link_opened',
  intelligenceNotificationPreferencesUpdated: 'intelligence_notification_preferences_updated',
  intelligenceAgencyEmailClicked: 'intelligence_agency_email_clicked',
  intelligenceRouteUrlOpened: 'intelligence_route_url_opened',

  // Intelligence V2
  intelligenceV2AgencyEmailClicked: 'intelligence_v2_agency_email_clicked',
  intelligenceV2AgencyLinkOpened: 'intelligence_v2_agency_link_opened',
  intelligenceV2AgencyPdfDownloaded: 'intelligence_v2_agency_pdf_downloaded',
  intelligenceV2AgencyPdfDownloadFailed: 'intelligence_v2_agency_pdf_download_failed',
  intelligenceV2AgencyRowToggled: 'intelligence_v2_agency_row_toggled',
  intelligenceV2ConflictEmailClicked: 'intelligence_v2_conflict_email_clicked',
  intelligenceV2ConflictLinkOpened: 'intelligence_v2_conflict_link_opened',
  intelligenceV2ConflictPdfDownloaded: 'intelligence_v2_conflict_pdf_downloaded',
  intelligenceV2ConflictPopoverOpened: 'intelligence_v2_conflict_popover_opened',
  intelligenceV2ConflictSectionJumped: 'intelligence_v2_conflict_section_jumped',
  intelligenceV2ConstructionDatesChanged: 'intelligence_v2_construction_dates_changed',
  intelligenceV2FeatureDetailOpened: 'intelligence_v2_feature_detail_opened',
  intelligenceV2FeatureSelected: 'intelligence_v2_feature_selected',
  intelligenceV2FetchFailed: 'intelligence_v2_fetch_failed',
  intelligenceV2NotesExpanded: 'intelligence_v2_notes_expanded',
  intelligenceV2NotesUpdated: 'intelligence_v2_notes_updated',
  intelligenceV2OverviewEditCancelled: 'intelligence_v2_overview_edit_cancelled',
  intelligenceV2OverviewEditStarted: 'intelligence_v2_overview_edit_started',
  intelligenceV2PlanStatusChanged: 'intelligence_v2_plan_status_changed',
  intelligenceV2ProjectInfoLinkClicked: 'intelligence_v2_project_info_link_clicked',
  intelligenceV2RouteUrlOpened: 'intelligence_v2_route_url_opened',
  intelligenceV2SubsectionExpanded: 'intelligence_v2_subsection_expanded',
  intelligenceV2TabSwitched: 'intelligence_v2_tab_switched',
  intelligenceV2TabSwitchRendered: 'intelligence_v2_tab_switch_rendered',

  // Potholing exports
  potholingCsvDownloaded: 'potholing_csv_downloaded',
  potholingKmzDownloaded: 'potholing_kmz_downloaded',
  potholingKmzDownloadFailed: 'potholing_kmz_download_failed',
  potholingPdfDownloaded: 'potholing_pdf_downloaded',
  potholingPdfDownloadFailed: 'potholing_pdf_download_failed',

  // CivilSearch drawers
  civilsearchActivityFeedViewed: 'civilsearch_activity_feed_viewed',
  civilsearchMessagesViewed: 'civilsearch_messages_viewed',

  // Admin — subscription feature provisioning
  adminSubscriptionFeatureAccessUpdated: 'admin_subscription_feature_access_updated',

  // Admin — restricted export escalations queue
  exportEscalationViewed: 'export_escalation_viewed',
  exportEscalationNotesSaved: 'export_escalation_notes_saved',
  exportEscalationArchived: 'export_escalation_archived',
  exportEscalationRunTriggered: 'export_escalation_run_triggered',

  // Export to KMZ
  exportToKMZ: 'export_to_kmz',
  exportToKMZFailed: 'export_to_kmz_failed',

  // Data Requests
  fileDownloaded: 'file_downloaded',
  fileDownloadFailed: 'file_download_failed',

  // Download Projects Table
  exportProjectsTable: 'export_projects_table',
  exportProjectsTableFailed: 'export_projects_table_failed',

  // Accessibility
  skipLinkActivated: 'skip_link_activated',
  projectDetailsPanelResized: 'project_details_panel_resized',

  // Visualize Geo-referenced maps
  georeferencedMapLayerToggled: 'georef_map_layer_toggled',
  georeferencedMapLayerSectionToggled: 'georef_map_layer_section_toggled',
  georeferencedMapLayerSectionExpanded: 'georef_map_layer_section_expanded',
} as const;

export type TPosthogEvent = (typeof POSTHOG_EVENTS)[keyof typeof POSTHOG_EVENTS];

export type ProjectViewMode = 'split' | 'table' | 'map';

export enum CivilDrawObjectType {
  Text = 'text',
  Stamp = 'stamp',
  Image = 'image',
  Polygon = 'polygon',
  Circle = 'circle',
  Arrow = 'arrow',
  Line = 'line',
  Measurement = 'measurement',
  Marker = 'marker',
  StreetViewMarker = 'street_view_marker',
  Comment = 'comment',
  Other = 'other',
}

// Collapse MapObject.type into a small union so PostHog charts don't fragment (e.g. text vs textV2).
export function mapObjectTypeToCivildrawObjectType(type?: MapObject['type']): CivilDrawObjectType {
  switch (type) {
    case 'text':
    case 'textV2':
      return CivilDrawObjectType.Text;
    case 'stamp':
      return CivilDrawObjectType.Stamp;
    case 'image':
      return CivilDrawObjectType.Image;
    case 'polygon':
      return CivilDrawObjectType.Polygon;
    case 'circle':
      return CivilDrawObjectType.Circle;
    case 'line':
      return CivilDrawObjectType.Line;
    case 'arrow':
      return CivilDrawObjectType.Arrow;
    case 'marker':
      return CivilDrawObjectType.Marker;
    case 'comment':
      return CivilDrawObjectType.Comment;
    case 'streetViewMarker':
      return CivilDrawObjectType.StreetViewMarker;
    default:
      return CivilDrawObjectType.Other;
  }
}

export type MetadataSource = 'toc_picker' | 'layer_detail' | 'project_info';

export type LayerLibraryResetFallbackTarget = 'org' | 'civilgrid';

/** Where a stored-file download started; Utilities successes are tracked as `utilities_file_downloaded`. */
export type FileDownloadSource = 'ceii' | 'evidence' | 'chat_attachment' | 'supporting_file' | 'utilities';

export enum KmzUploadErrorReason {
  General = 'general',
  MissingFile = 'missing_file',
  NoContent = 'no_content',
  NoFeatures = 'no_features',
  MixedGeometryTypes = 'mixed_geometry_types',
  InvalidGeometryType = 'invalid_geometry_type',
  InvalidPolygonGeometry = 'invalid_polygon_geometry',
  InvalidLineStringGeometry = 'invalid_line_string_geometry',
  TooManySegments = 'too_many_segments',
}

/** Where a utilities report was started. */
export type UtilitiesReportsSource = 'exports_menu' | 'reports_tab';

/** Where the utility contacts list was opened. */
export type UtilityContactsSource = 'contacts_button' | 'reports_tab';

/** How the user reached the Exports tab. */
export type ExportEntryPoint = 'utilities_exports_menu' | 'other';

/** How the "Reports have moved" popover was closed. */
export type ReportsMovedPopoverAction = 'show_me' | 'got_it' | 'dismissed';

export type UtilitiesExportsMenuItem =
  | 'gis_cad_data'
  | 'outreach_log'
  | 'records_research_report'
  | 'communication_log'
  | 'download_all_files';

/** Where a Contact support click came from; add a value here for each new call site. */
export type ContactSupportSource = 'org_members_support_access_card' | 'connectbase_agencies_failed';

interface BaseProjectProps {
  project_id: string;
  organization_id?: string;
}

type ExportLayersBulkToggledProps = BaseProjectProps & {
  // Only the rows on screen: under a search, the matches rather than the whole group
  affected_layer_count: number;
  search_active: boolean;
};

export interface PosthogEventPropertyMap {
  // project_id optional: fires from /home (no project) and project-details. source disambiguates.
  [POSTHOG_EVENTS.projectBasemapChanged]: Omit<BaseProjectProps, 'project_id'> & {
    project_id?: string;
    source: 'home' | 'project_details';
    from: string;
    to: string;
  };
  // project_id optional: fires from /home (no project) and project-details. source disambiguates.
  [POSTHOG_EVENTS.projectMapViewToggled]: Omit<BaseProjectProps, 'project_id'> & {
    project_id?: string;
    source: 'home' | 'project_details';
    from: ProjectViewMode;
    to: ProjectViewMode;
  };
  [POSTHOG_EVENTS.projectLayerFilterChanged]: BaseProjectProps & {
    filter_type: string;
    value: string | number | boolean | null;
    action: 'add' | 'remove' | 'set' | 'clear';
  };

  [POSTHOG_EVENTS.projectEnvironmentalLayerToggled]: BaseProjectProps & {
    layer_id: string;
    layer_name?: string;
    enabled: boolean;
    source: 'toc' | 'env_tab';
  };
  [POSTHOG_EVENTS.projectGeotechLayerToggled]: BaseProjectProps & {
    layer_id: string;
    layer_name?: string;
    enabled: boolean;
    source: 'toc' | 'geotech_tab';
  };

  [POSTHOG_EVENTS.projectCivildrawDraftCreated]: BaseProjectProps & {
    draft_id?: string;
    source?: string;
  };
  [POSTHOG_EVENTS.projectCivildrawDrawingCreated]: BaseProjectProps & {
    drawing_id: string;
    object_type?: CivilDrawObjectType;
    civildraw_use_new_drawing_canvas: boolean;
  };
  [POSTHOG_EVENTS.projectCivildrawObjectAdded]: BaseProjectProps & {
    object_type: CivilDrawObjectType;
    civildraw_use_new_drawing_canvas: boolean;
  };
  [POSTHOG_EVENTS.projectCivildrawPrintCreated]: BaseProjectProps & {
    print_format?: string;
    map_id?: string;
  };

  // CivilDraw (formerly "GEK") — map/object/comment/file actions
  [POSTHOG_EVENTS.civildrawMapCreated]: BaseProjectProps & {
    map_id: string;
  };
  [POSTHOG_EVENTS.civildrawMapDeleted]: BaseProjectProps & {
    map_id: string;
    placement: string;
  };
  [POSTHOG_EVENTS.civildrawMapDuplicated]: BaseProjectProps & {
    map_id: string;
  };
  [POSTHOG_EVENTS.civildrawUnitsToggled]: BaseProjectProps & {
    measurement_unit: 'imperial' | 'metric';
    map_id?: string;
  };
  // project_id optional: fires from the map context menu, which also runs on the non-project map.
  [POSTHOG_EVENTS.civildrawOpenedInGoogleMaps]: Omit<BaseProjectProps, 'project_id'> & {
    project_id?: string;
    map_id?: string;
  };
  [POSTHOG_EVENTS.civildrawActionUndone]: BaseProjectProps & {
    placement: 'key binding' | 'menu';
    map_id?: string;
  };
  [POSTHOG_EVENTS.civildrawActionRedone]: BaseProjectProps & {
    placement: 'key binding' | 'menu';
    map_id?: string;
  };
  [POSTHOG_EVENTS.civildrawObjectDeleted]: BaseProjectProps & {
    object_type: CivilDrawObjectType;
    map_id?: string;
    civildraw_use_new_drawing_canvas: boolean;
  };
  [POSTHOG_EVENTS.civildrawObjectVisibilityToggled]: BaseProjectProps & {
    object_type: CivilDrawObjectType;
    hidden: boolean;
    map_id?: string;
  };
  [POSTHOG_EVENTS.civildrawAllObjectsVisibilityToggled]: BaseProjectProps & {
    hidden: boolean;
    map_id?: string;
  };
  [POSTHOG_EVENTS.civildrawObjectStylingUpdated]: BaseProjectProps & {
    object_type: CivilDrawObjectType;
    styling_property: string;
    map_id?: string;
  };
  [POSTHOG_EVENTS.civildrawObjectGeometryUpdated]: BaseProjectProps & {
    object_type: CivilDrawObjectType;
    map_id?: string;
    civildraw_use_new_drawing_canvas: boolean;
  };
  [POSTHOG_EVENTS.civildrawObjectFlownTo]: BaseProjectProps & {
    object_type: CivilDrawObjectType;
    map_id?: string;
  };
  [POSTHOG_EVENTS.civildrawCommentAdded]: BaseProjectProps & {
    map_id?: string;
  };
  [POSTHOG_EVENTS.civildrawCommentDeleted]: BaseProjectProps & {
    map_id?: string;
  };
  // project_id optional: fires from the map context menu, which also runs on the non-project map.
  [POSTHOG_EVENTS.civildrawCoordinatesCopied]: Omit<BaseProjectProps, 'project_id'> & {
    project_id?: string;
    map_id?: string;
  };
  [POSTHOG_EVENTS.civildrawObjectCoordinatesCopied]: BaseProjectProps & {
    object_type: CivilDrawObjectType;
    map_id?: string;
  };
  [POSTHOG_EVENTS.civildrawFileAdded]: BaseProjectProps & {
    object_type: CivilDrawObjectType;
    map_id?: string;
  };
  [POSTHOG_EVENTS.civildrawFileDeleted]: BaseProjectProps & {
    object_type: CivilDrawObjectType;
    map_id?: string;
  };
  [POSTHOG_EVENTS.civildrawFileDownloaded]: BaseProjectProps & {
    object_type: CivilDrawObjectType;
    map_id?: string;
  };
  // The failure counterpart of civildrawFileDownloaded, carrying the same identifying properties.
  [POSTHOG_EVENTS.civildrawFileDownloadFailed]: BaseProjectProps & {
    object_type: CivilDrawObjectType;
    map_id?: string;
    reason: string;
  };
  [POSTHOG_EVENTS.civildrawFileImported]: BaseProjectProps & {
    map_id?: string;
  };

  [POSTHOG_EVENTS.projectFileDownloaded]: BaseProjectProps & {
    file_id?: string;
    file_name: string;
    file_type: string;
    file_size?: number;
    /**
     * How the file reached the user, which is also what this event proves. 'blob' is captured once
     * the bytes are in the page, so it counts saved files and carries a `file_size`. 'browser' is
     * captured once the server has signed for the object, so it counts confirmed hand-offs rather
     * than finished saves. Split on this before comparing sources or building a funnel.
     */
    delivery: 'blob' | 'browser';
    source?: string;
    // Set when the download is a whole export run rather than one file, so run downloads can be
    // told apart from per-file ones and tied back to the submission that produced them.
    submission_id?: string;
    export_types?: string[];
  };
  // The failure counterpart of projectFileDownloaded, carrying the same identifying properties so
  // the two can be compared directly. A file size is never known, since nothing was retrieved.
  [POSTHOG_EVENTS.projectFileDownloadFailed]: BaseProjectProps & {
    file_id?: string;
    file_name: string;
    file_type: string;
    /**
     * Matches projectFileDownloaded, so the pair can be compared within one delivery mechanism.
     * On 'browser' this is a signature our API refused; a hand-off it accepted never reports back.
     */
    delivery: 'blob' | 'browser';
    source?: string;
    submission_id?: string;
    export_types?: string[];
    /** An HTTP status when the response arrived, otherwise a network or browser failure. */
    reason: string;
  };
  [POSTHOG_EVENTS.exportToKMZ]: BaseProjectProps;
  [POSTHOG_EVENTS.exportToKMZFailed]: BaseProjectProps & {
    reason: string;
  };
  // The success counterpart of fileDownloadFailed. Fires when the browser is handed the signed file,
  // not when the save finishes; file_size is the S3 object size in bytes, when known.
  [POSTHOG_EVENTS.fileDownloaded]: {
    source: Exclude<FileDownloadSource, 'utilities'>;
    file_name: string;
    file_id?: string;
    project_id?: string;
    request_id?: string;
    file_size?: number;
  };
  [POSTHOG_EVENTS.fileDownloadFailed]: {
    source: FileDownloadSource;
    file_name?: string;
    /** An HTTP status when the response arrived, otherwise a network or browser failure. */
    reason: string;
  };
  [POSTHOG_EVENTS.projectProposalPacketExported]: BaseProjectProps & {
    packet_id?: string;
    format?: string;
  };
  [POSTHOG_EVENTS.projectProposalPacketExportFailed]: BaseProjectProps & {
    packet_id?: string;
    format?: string;
    /** An HTTP status when the response arrived, otherwise a network or browser failure. */
    reason: string;
  };
  [POSTHOG_EVENTS.projectExportInitiated]: BaseProjectProps & {
    // The disambiguator across every export flow. Stays the single lowercased format for a
    // one-format export; a multi-format submission reports 'multi' and details it in `export_types`,
    // so existing single-format breakdowns keep their meaning.
    export_type: string;
    export_types?: string[];
    // Extract (CAD/GIS) form only.
    entry_point?: ExportEntryPoint;
    export_format_count?: number;
    // Ties the event to the export's download records.
    submission_id?: string;
    extract_template_id?: string | null;
    extract_template_name?: string | null;
    projection_id?: string | null;
    projection_name?: string | null;
    source?: string;
    selected_layer_ids?: string[] | null;
    cc_recipient_count?: number;
  };
  [POSTHOG_EVENTS.projectExportFailed]: BaseProjectProps & {
    /** An HTTP status when the response arrived, otherwise a network or browser failure. */
    reason: string;
  };
  [POSTHOG_EVENTS.projectExportGuideOpened]: BaseProjectProps;
  [POSTHOG_EVENTS.exportHistoryLoaded]: BaseProjectProps & {
    phase: 'initial' | 'poll';
    outcome: 'success' | 'failure';
    duration_ms: number;
  };
  [POSTHOG_EVENTS.exportTabLoaded]: BaseProjectProps & {
    entry_point: ExportEntryPoint;
    duration_ms: number;
    form_ready_ms: number;
    history_outcome: 'success' | 'failure';
  };
  [POSTHOG_EVENTS.exportHistorySlowLoadDetected]: BaseProjectProps & {
    phase: 'initial' | 'poll';
    elapsed_ms: number;
  };
  [POSTHOG_EVENTS.exportHistoryAwaitingSubmissionTimedOut]: BaseProjectProps & {
    submission_id: string;
  };
  [POSTHOG_EVENTS.exportFormDataLoadFailed]: BaseProjectProps & {
    resource: 'projections' | 'templates' | 'project' | 'restrictions' | 'digitization_access' | 'layer_asset_presence';
  };
  [POSTHOG_EVENTS.exportFormProjectSlowLoadDetected]: BaseProjectProps;
  [POSTHOG_EVENTS.exportLimitsCheckRetried]: BaseProjectProps;
  [POSTHOG_EVENTS.exportCustomDataLoadFailed]: BaseProjectProps & {
    surface: 'form' | 'history';
  };
  [POSTHOG_EVENTS.exportLayerSearchChanged]: BaseProjectProps & {
    action: 'set' | 'clear';
    // Rows still listed after the query; absent on a clear
    within_match_count?: number;
    outside_match_count?: number;
    matched_layer_ids?: string[];
  };
  [POSTHOG_EVENTS.exportLayersBulkToggledOn]: ExportLayersBulkToggledProps;
  [POSTHOG_EVENTS.exportLayersBulkToggledOff]: ExportLayersBulkToggledProps;
  [POSTHOG_EVENTS.exportFormRebuilt]: BaseProjectProps & {
    // The replayed run's submission; null for a run that predates submissions
    submission_id: string | null;
    restored_layer_count: number;
    dropped_layer_count: number;
    dropped_layer_ids: string[];
    formats_restored: boolean;
    projection_restored: boolean;
    template_restored: boolean;
  };

  [POSTHOG_EVENTS.mapExportFailed]: {
    title: string;
    notes: string;
    format: string;
    /** An HTTP status when the response arrived, otherwise the caught error message. */
    reason: string;
  };
  [POSTHOG_EVENTS.organizationBillingSummaryPrintFailed]: {
    organization_id: string;
    date_range: string;
    type: 'docx' | 'pdf';
    /** An HTTP status when the response arrived, otherwise a network or browser failure. */
    reason: string;
  };
  [POSTHOG_EVENTS.projectQuotePrintFailed]: BaseProjectProps & {
    format: 'docx' | 'pdf';
    /** An HTTP status when the response arrived, otherwise a network or browser failure. */
    reason: string;
  };
  [POSTHOG_EVENTS.projectReceiptPrintFailed]: BaseProjectProps & {
    format: 'docx' | 'pdf';
    /** An HTTP status when the response arrived, otherwise a network or browser failure. */
    reason: string;
  };
  [POSTHOG_EVENTS.projectUtilitiesAttachmentsDownloaded]: BaseProjectProps & {
    cc_recipient_count: number;
  };
  [POSTHOG_EVENTS.projectUtilitiesAttachmentsDownloadFailed]: BaseProjectProps & {
    /** An HTTP status when the response arrived, otherwise a network or browser failure. */
    reason: string;
  };
  [POSTHOG_EVENTS.intelligenceAgencyPdfDownloadFailed]: BaseProjectProps & {
    agency_id?: string;
    /** An HTTP status when the response arrived, otherwise a network or browser failure. */
    reason: string;
  };
  [POSTHOG_EVENTS.intelligenceV2AgencyPdfDownloadFailed]: BaseProjectProps & {
    agency_id?: string;
    /** An HTTP status when the response arrived, otherwise a network or browser failure. */
    reason: string;
  };
  [POSTHOG_EVENTS.potholingCsvDownloaded]: BaseProjectProps & {
    row_count: number;
  };
  [POSTHOG_EVENTS.potholingKmzDownloadFailed]: BaseProjectProps & {
    /** An HTTP status when the response arrived, otherwise a network or browser failure. */
    reason: string;
  };
  [POSTHOG_EVENTS.potholingPdfDownloadFailed]: BaseProjectProps & {
    /** An HTTP status when the response arrived, otherwise a network or browser failure. */
    reason: string;
  };

  [POSTHOG_EVENTS.projectFieldNoteAdded]: BaseProjectProps & {
    note_id?: string;
  };
  [POSTHOG_EVENTS.projectFieldNoteOpened]: BaseProjectProps & {
    note_id: string;
    note_type: 'note' | 'image' | 'video';
    source: 'grid' | 'map' | 'deeplink';
  };
  [POSTHOG_EVENTS.projectFieldNoteEdited]: BaseProjectProps & {
    note_id: string;
    note_type: 'note';
  };
  [POSTHOG_EVENTS.projectFieldNoteDeleted]: BaseProjectProps & {
    note_id: string;
    note_type: 'note' | 'image' | 'video';
  };
  [POSTHOG_EVENTS.projectFieldNotesExported]: BaseProjectProps & {
    file_type: 'pdf' | 'zip';
    item_count: number;
  };
  [POSTHOG_EVENTS.projectFieldNotesTabViewed]: BaseProjectProps;
  [POSTHOG_EVENTS.projectFieldNotePhotoFullscreenToggled]: BaseProjectProps & {
    note_id: string;
    action: 'entered' | 'exited';
  };
  [POSTHOG_EVENTS.projectFieldNotePhotoZoomed]: BaseProjectProps & {
    note_id: string;
    direction: 'in' | 'out';
  };
  [POSTHOG_EVENTS.projectFieldNotePhotoPanned]: BaseProjectProps & {
    note_id: string;
    direction: 'up' | 'down' | 'left' | 'right';
    method: 'button' | 'keyboard';
  };
  [POSTHOG_EVENTS.projectPhotoImportOpened]: BaseProjectProps;
  [POSTHOG_EVENTS.projectPhotoImportZipRead]: BaseProjectProps & {
    // Kept as the zip-read name from when a zip was the only way in; `source` is what distinguishes
    // the three intakes now.
    source: 'zip' | 'files' | 'mixed';
    zip_count: number;
    loose_file_count: number;
    total_images: number;
    geotagged_count: number;
    skipped_count: number;
  };
  [POSTHOG_EVENTS.projectPhotoImportStarted]: BaseProjectProps & {
    selected_count: number;
    out_of_area_count: number;
    has_note: boolean;
    is_retry: boolean;
  };
  [POSTHOG_EVENTS.projectPhotoImportCompleted]: BaseProjectProps & {
    attempted_count: number;
    created_count: number;
    failed_count: number;
    aborted: boolean;
  };
  [POSTHOG_EVENTS.projectIntelligenceTabViewed]: BaseProjectProps & {
    tab_id?: string;
  };

  // Discriminated union: layer_id is required iff metadata_source === 'layer_detail'.
  // project_id optional: also fires on the non-project (home/explore) map. Absence signals global.
  [POSTHOG_EVENTS.projectMetadataOpened]: Omit<BaseProjectProps, 'project_id'> & { project_id?: string } & (
      | { metadata_source: 'layer_detail'; layer_id: string }
      | { metadata_source: Exclude<MetadataSource, 'layer_detail'> }
    );

  // Layer Library surface (CG-512). organization_id is optional because the active org
  // can still be resolving when the page mounts. `scope` is the active scope tab.
  // The search event intentionally omits the raw query string (free-text PII hygiene).
  [POSTHOG_EVENTS.layerLibraryOpened]: { organization_id?: string };
  [POSTHOG_EVENTS.layerLibraryClosed]: { organization_id?: string };
  [POSTHOG_EVENTS.layerLibraryLayerAdded]: { layer_id: string; scope: string; organization_id?: string };
  [POSTHOG_EVENTS.layerLibraryLayerRemoved]: { layer_id: string; scope: string; organization_id?: string };
  [POSTHOG_EVENTS.layerLibraryReset]: {
    removed_count: number;
    organization_id?: string;
    fallback_target: LayerLibraryResetFallbackTarget;
  };
  [POSTHOG_EVENTS.layerLibraryScopeChanged]: { from: string; to: string; organization_id?: string };
  [POSTHOG_EVENTS.layerLibrarySearched]: { organization_id?: string };
  // No properties — the link target (org settings) is fixed and the click carries no other context.
  [POSTHOG_EVENTS.layerLibraryOrgLinkClicked]: Record<string, never>;

  // Org Layer Library surface (CG-724). organization_id optional (may still be resolving).
  [POSTHOG_EVENTS.orgLayerLibraryOpened]: { organization_id?: string };
  [POSTHOG_EVENTS.orgLayerLibraryLayerAdded]: { layer_id: string; scope: string; organization_id?: string };
  [POSTHOG_EVENTS.orgLayerLibraryLayerRemoved]: { layer_id: string; scope: string; organization_id?: string };
  [POSTHOG_EVENTS.orgLayerLibraryReset]: { removed_count: number; organization_id?: string };
  [POSTHOG_EVENTS.orgLayerLibraryScopeChanged]: { from: string; to: string; organization_id?: string };
  [POSTHOG_EVENTS.orgLayerLibrarySearched]: { organization_id?: string };

  // Layer picker bookmark + section expansion. No organization_id — consistent with the
  // sibling map_layer_* visibility events, which rely on PostHog group properties for org.
  [POSTHOG_EVENTS.layerPinned]: { layer_id: string };
  [POSTHOG_EVENTS.layerUnpinned]: { layer_id: string };
  [POSTHOG_EVENTS.layerSectionExpanded]: { section_id: string; expanded: boolean };

  // Custom data
  [POSTHOG_EVENTS.customDataFeatureInspected]: BaseProjectProps & {
    layer_id: string;
    category: string;
  };

  [POSTHOG_EVENTS.customDataShapefileSubmitted]: BaseProjectProps & {
    custom_data_id: string;
  };

  [POSTHOG_EVENTS.customDataCategoryColorsSet]: BaseProjectProps & {
    custom_data_id: string;
    category_count: number;
    changed_count: number;
    renamed_count: number;
  };

  [POSTHOG_EVENTS.customDataWmtsInspected]: BaseProjectProps & {
    layer_count: number;
    hostname: string;
  };

  [POSTHOG_EVENTS.exportProjectsTable]: {
    organization_id: string;
  };
  [POSTHOG_EVENTS.exportProjectsTableFailed]: {
    organization_id: string;
    reason: string;
  };

  // Fires when the user checks "Confidential Project" and the confirmation prompt appears.
  [POSTHOG_EVENTS.projectConfidentialWarningShown]: BaseProjectProps;
  // Fires when the user resolves the warning — confirms the flag or backs out.
  [POSTHOG_EVENTS.projectConfidentialWarningResolved]: BaseProjectProps & {
    decision: 'confirmed' | 'removed';
  };

  // Saved project-details edit where the user made themselves the project manager.
  // `source` separates the "Assign to me" shortcut from picking yourself in the select.
  [POSTHOG_EVENTS.projectManagerSelfAssigned]: BaseProjectProps & {
    previous_project_manager_id: string | null;
    source: 'assign_to_me_button' | 'project_manager_select';
  };

  [POSTHOG_EVENTS.orgSpecialDatasetsUpdated]: {
    organization_id: string;
    special_datasets: string[];
    special_datasets_count: number;
  };
  [POSTHOG_EVENTS.tileLayersDisabledUpdated]: {
    disabled_count: number;
  };
  [POSTHOG_EVENTS.adminSubscriptionFeatureAccessUpdated]: {
    organization_id: string;
    overridden_features: string[];
    disabled_tiers: string[];
  };
  [POSTHOG_EVENTS.demoOrgResetRequested]: {
    organization_id: string;
  };

  // Admin — export escalations queue
  [POSTHOG_EVENTS.exportEscalationViewed]: { escalation_id: string; project_id: string; escalation_reason: string };
  [POSTHOG_EVENTS.exportEscalationNotesSaved]: { escalation_id: string; escalation_reason: string };
  [POSTHOG_EVENTS.exportEscalationArchived]: {
    escalation_id: string;
    previous_status: string;
    escalation_reason: string;
  };
  [POSTHOG_EVENTS.exportEscalationRunTriggered]: { escalation_id: string; escalation_reason: string };

  // Utilities tab load telemetry. load_phase distinguishes the agency-population
  // polling phase (job_poll) from the data fetch that runs once population completes.
  [POSTHOG_EVENTS.utilitiesTabLoaded]: {
    project_id: string;
    load_phase: 'job_poll' | 'data_fetch';
    outcome: 'completed' | 'failed' | 'timed_out';
    duration_ms: number;
    poll_attempts?: number;
    task_status?: string;
  };
  [POSTHOG_EVENTS.utilitiesTabSlowLoadDetected]: {
    project_id: string;
    load_phase: 'job_poll' | 'data_fetch';
    elapsed_ms: number;
    poll_attempts?: number;
    task_status?: string;
  };
  [POSTHOG_EVENTS.connectbaseStatusMessageShown]: {
    project_id: string;
    organization_id?: string;
    task: ConnectbaseTaskKey;
    status: ConnectbaseNoticeStatus;
  };
  // User cycled the Est time column sort to the given mode (source: main table or select-assets step).
  [POSTHOG_EVENTS.utilitiesEstTimeSortChanged]: {
    project_id: string;
    sort_mode: 'time' | 'confidence' | 'off';
    source: 'table' | 'select_assets';
  };
  // Individual agency file handed to the browser from /utilities (Documents column, messages, activity,
  // or the legacy request-status dialog). Counts a signed hand-off, not a finished save; file_size is
  // the S3 object size in bytes.
  [POSTHOG_EVENTS.utilitiesFileDownloaded]: {
    project_id: string;
    file_id?: string;
    file_name: string;
    file_type: string;
    file_size: number;
  };
  // Metadata sidebar URL attribute opened in a new tab; url_host is the hostname only, never the
  // full URL. project_id optional: the sidebar also runs on the non-project home/explore map.
  [POSTHOG_EVENTS.mapMetadataLinkOpened]: Omit<BaseProjectProps, 'project_id'> & {
    project_id?: string;
    layer_id: string;
    field_label: string;
    url_host: string;
  };

  [POSTHOG_EVENTS.environmentalSwpppExported]: BaseProjectProps;
  [POSTHOG_EVENTS.environmentalSwpppExportFailed]: BaseProjectProps & {
    reason: string;
  };
  [POSTHOG_EVENTS.environmentalStudyPDFDownloaded]: {
    project_id: string;
    file_count: number;
  };
  [POSTHOG_EVENTS.environmentalStudyPDFDownloadFailed]: {
    project_id: string;
    reason: string;
  };
  [POSTHOG_EVENTS.femaFloodReportGenerated]: {
    project_id: string;
  };

  [POSTHOG_EVENTS.femaFloodReportGenerationFailed]: {
    project_id: string;
    reason: string;
  };

  [POSTHOG_EVENTS.projectBoundaryKMZUploaded]: {
    organization_id?: string;
  };
  // The failure counterpart of projectBoundaryKMZUploaded. error_reason is the stable key to group
  // by; error_message is the text the user saw. error_details carries the thrown error for the general case.
  [POSTHOG_EVENTS.projectBoundaryKMZUploadFailed]: {
    organization_id?: string;
    error_reason: KmzUploadErrorReason;
    error_message: string;
    error_details?: string;
  };
  [POSTHOG_EVENTS.civilsearchRecordsResearchReportGenerated]: BaseProjectProps & {
    source: UtilitiesReportsSource;
  };
  [POSTHOG_EVENTS.civilsearchRequestResearchClicked]: BaseProjectProps;
  [POSTHOG_EVENTS.utilitiesExportsMenuItemSelected]: BaseProjectProps & {
    item: UtilitiesExportsMenuItem;
  };
  [POSTHOG_EVENTS.utilitiesReportsMovedPopoverOpened]: BaseProjectProps & {
    on_utilities: boolean;
  };
  [POSTHOG_EVENTS.utilitiesReportsMovedPopoverClosed]: BaseProjectProps & {
    action: ReportsMovedPopoverAction;
    on_utilities: boolean;
  };
  [POSTHOG_EVENTS.utilitiesContactEmailCopied]: BaseProjectProps & {
    agency_ids: string[];
  };
  [POSTHOG_EVENTS.utilitiesContactEmailClicked]: BaseProjectProps & {
    agency_ids: string[];
  };
  [POSTHOG_EVENTS.utilitiesContactsZeroStateViewed]: BaseProjectProps;
  [POSTHOG_EVENTS.utilitiesContactsDownloaded]: BaseProjectProps & {
    contact_count: number;
    agency_count: number;
    // true when the search or Type filter narrowed the download
    filtered: boolean;
  };
  [POSTHOG_EVENTS.utilitiesContactsSearched]: BaseProjectProps & {
    query_length: number;
    contact_count: number;
  };
  [POSTHOG_EVENTS.utilitiesContactsTypeFilterChanged]: BaseProjectProps & {
    asset_types: string[];
    contact_count: number;
  };
  [POSTHOG_EVENTS.utilitiesNotificationPreferencesUpdated]: BaseProjectProps & {
    project_weekly_digest: boolean;
    new_requests_created: boolean;
    new_messages_created: boolean;
    request_documents_added: boolean;
    request_status_updates: boolean;
    request_complete_only: boolean;
  };
  [POSTHOG_EVENTS.civilsearchAgencyCommunicationLogGenerated]: BaseProjectProps & {
    source: UtilitiesReportsSource;
    request_ids: Awaited<ReturnType<typeof generateEvidenceEmailChain>>['requestIds'];
  };
  [POSTHOG_EVENTS.organizationInvoicePrinted]: {
    organization_id: string;
    project_ids: string[];
  };
  [POSTHOG_EVENTS.organizationInvoicePrintFailed]: {
    organization_id: string;
    project_ids: string[];
    reason: string;
  };
  [POSTHOG_EVENTS.contactSupportClicked]: {
    organization_id: string;
    source: ContactSupportSource;
  };
  [POSTHOG_EVENTS.georeferencedMapSourceDeepLinkPdfOpened]: BaseProjectProps & {
    agency_id: string;
    source_file_id: string;
    data_request_id: string | null;
    access: GeoreferencedMapAccess;
  };
  [POSTHOG_EVENTS.georeferencedMapSourcePdfOpened]: BaseProjectProps & {
    agency_id: string;
    source_file_id: string;
    data_request_id: string | null;
    access: GeoreferencedMapAccess;
  };
  [POSTHOG_EVENTS.georeferencedMapLayerToggled]: BaseProjectProps & {
    layer_id: string;
    action: string;
  };
  [POSTHOG_EVENTS.georeferencedMapLayerSectionToggled]: BaseProjectProps & {
    layer_section_id: string;
    action: string;
    child_layer_ids: string[];
  };
  [POSTHOG_EVENTS.georeferencedMapLayerSectionExpanded]: BaseProjectProps & {
    layer_section_id: string;
    expanded: boolean;
  };
  [POSTHOG_EVENTS.landRightsDataDownloaded]: BaseProjectProps & {
    type: string;
    doc_count?: number;
  };
  [POSTHOG_EVENTS.landRightsDataDownloadFailed]: BaseProjectProps & {
    type: string;
    reason?: string;
    doc_count?: number;
  };
}

export type TypedPosthogEvent = keyof PosthogEventPropertyMap;

/**
 * Typed wrapper around `capturePosthogEvent` for events whose property shape is declared
 * in `PosthogEventPropertyMap`. Prefer this for project_* events; for untyped events still
 * call `capturePosthogEvent` directly with a `POSTHOG_EVENTS.*` value.
 */
export function captureTypedPosthogEvent<E extends TypedPosthogEvent>(
  event: E,
  properties: PosthogEventPropertyMap[E],
): void {
  capturePosthogEvent(event, properties as unknown as Record<string, unknown>);
}
